import { useState } from "react";
import axios from "axios";
import { StarInput } from "@/components/Stars";
import { SERVICE_OPTIONS } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  email: "",
  company: "",
  role: "",
  service: "",
  rating: 0,
  text: "",
  website: "", // honeypot — must stay empty
};

const FIELD =
  "w-full rounded border border-line bg-white px-4 py-2.5 text-sm text-steel placeholder:text-slate-400 focus:border-royal focus:outline-none transition-colors";
const LABEL = "block text-xs font-mono-sub text-mute mb-1 uppercase tracking-wider";

/** Public review submission — stored as "pending" until approved by admin. */
export function ReviewForm() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [feedback, setFeedback] = useState("");

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.rating) {
      setStatus("error");
      setFeedback("Please select a star rating.");
      return;
    }
    setStatus("submitting");
    setFeedback("");
    try {
      const res = await axios.post(`${API}/reviews`, form);
      setStatus("success");
      setFeedback(res.data.message);
    } catch (err) {
      setStatus("error");
      const detail = err.response?.data?.detail;
      setFeedback(
        typeof detail === "string"
          ? detail
          : "Something went wrong while sending your review. Please try again.",
      );
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid="review-success-message"
        className="rounded-2xl border border-royal/30 bg-royal-light/50 p-8 text-center"
      >
        <p className="font-poster text-xl font-bold text-steel">Review received</p>
        <p className="mt-3 text-sm leading-relaxed text-ink">{feedback}</p>
        <button
          data-testid="review-send-another-button"
          onClick={() => {
            setForm(EMPTY);
            setStatus("idle");
          }}
          className="mt-6 rounded border border-slate-300 px-6 py-2.5 text-sm text-slate-700 transition-colors hover:bg-white"
        >
          Write another review
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="review-form"
      onSubmit={handleSubmit}
      className="space-y-5 rounded-2xl border border-line bg-white p-6 shadow-sm sm:p-8"
    >
      <div>
        <span className={LABEL}>Your rating *</span>
        <StarInput
          value={form.rating}
          onChange={(rating) => setForm({ ...form, rating })}
          testId="review-rating-input"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="review-name" className={LABEL}>Name *</label>
          <input id="review-name" data-testid="review-name-input" name="name" type="text" required minLength={2} maxLength={80} value={form.name} onChange={update} className={FIELD} />
        </div>
        <div>
          <label htmlFor="review-email" className={LABEL}>Email * <span className="normal-case tracking-normal">(never published)</span></label>
          <input id="review-email" data-testid="review-email-input" name="email" type="email" required value={form.email} onChange={update} className={FIELD} />
        </div>
        <div>
          <label htmlFor="review-company" className={LABEL}>Company</label>
          <input id="review-company" data-testid="review-company-input" name="company" type="text" maxLength={120} value={form.company} onChange={update} className={FIELD} />
        </div>
        <div>
          <label htmlFor="review-role" className={LABEL}>Your role</label>
          <input id="review-role" data-testid="review-role-input" name="role" type="text" maxLength={80} value={form.role} onChange={update} className={FIELD} placeholder="e.g. Operations Director" />
        </div>
      </div>

      <div>
        <label htmlFor="review-service" className={LABEL}>Service you used</label>
        <select id="review-service" data-testid="review-service-select" name="service" value={form.service} onChange={update} className={FIELD}>
          <option value="">Select (optional)…</option>
          {SERVICE_OPTIONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="review-text" className={LABEL}>Your review *</label>
        <textarea id="review-text" data-testid="review-text-input" name="text" rows={5} required minLength={20} maxLength={1200} value={form.text} onChange={update} className={FIELD} placeholder="What did we build or support, and how did it go?" />
        <p className="mt-1 text-right font-mono-sub text-[11px] text-mute">{form.text.length}/1200</p>
      </div>

      {/* Honeypot: invisible to humans, attractive to bots. Never fill this. */}
      <input type="text" name="website" value={form.website} onChange={update} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" data-testid="review-honeypot-input" />

      {status === "error" && (
        <p data-testid="review-error-message" className="text-center text-sm text-red-600">{feedback}</p>
      )}

      <button
        type="submit"
        data-testid="review-submit-button"
        disabled={status === "submitting"}
        className="w-full rounded bg-steel py-3.5 text-sm font-bold text-white transition-colors hover:bg-steel-light disabled:opacity-60"
      >
        {status === "submitting" ? "Sending…" : "Submit Review"}
      </button>
    </form>
  );
}
